import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  InputNumber,
  Select,
  Tabs,
  Tag,
  Tooltip,
  Alert,
  Card,
  Empty,
  message,
} from "antd";
import {
  InfoCircleOutlined,
  ReloadOutlined,
  UserDeleteOutlined,
  WarningOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import dayjsTZ from "../../../utils/dayjs";
import { API } from "../../../helpers/API/amplifyApi";
import { ymToLabel } from "../utils/scheduleHelpers";

const TEAM_NONE = "_unteamed";

// Read the effective quota for (rule, team), preferring (in order):
//   1. per-month override stored on the schedule (shape: overrides[roleId][team]),
//   2. policy.teamQuotas[team],
//   3. flat policy fields (legacy).
const resolveQuota = (rule, team, overrides) => {
  const ovr = overrides?.[rule.roleId]?.[team];
  if (ovr) {
    return {
      minSeniors: Number(ovr.minSeniors || 0),
      minJuniors: Number(ovr.minJuniors || 0),
    };
  }
  const tq = rule?.teamQuotas?.[team];
  if (tq) {
    return {
      minSeniors: Number(tq.minSeniors || 0),
      minJuniors: Number(tq.minJuniors || 0),
    };
  }
  return {
    minSeniors: Number(
      rule?.minSeniorsAfternoonWeek ?? rule?.minPerAfternoonWeek ?? 0
    ),
    minJuniors: Number(rule?.minJuniorsAfternoonWeek ?? 0),
  };
};

const yearsOfService = (employee, asOfMs = Date.now()) => {
  const hire = Number(employee?.employeeHireDate || 0);
  if (!hire) return 0;
  return (asOfMs - hire) / (365.25 * 24 * 60 * 60 * 1000);
};

// Mirrors the backend eligibility filter so the admin sees the SAME
// senior/junior counts the algorithm will use.
const splitEligible = (employees, rule, team, excludedSet) => {
  const seniors = [];
  const juniors = [];
  const minYears = Number(rule?.minYearsExperience || 0);
  for (const e of employees) {
    if ((e?.employeeStatus || "").toLowerCase() !== "aktiv") continue;
    if (e?.employeeRoleName !== rule?.roleName) continue;
    if ((e?.employeeTeam || TEAM_NONE) !== team) continue;
    if (excludedSet?.has(e.employeeId)) continue;
    const y = yearsOfService(e);
    if (y >= minYears) seniors.push(e);
    else juniors.push(e);
  }
  return { seniors, juniors };
};

function MonthParamsDrawer({
  open,
  onClose,
  schedule,
  policy,
  employees = [],
  isDarkMode = false,
  onSaved,
  onSavedAndRegenerated,
  onEditPolicy,
}) {
  // In "everyone-once" mode the picker assigns each eligible employee
  // exactly one afternoon week, regardless of per-role quotas. So any
  // minSeniors/minJuniors number the admin sets here is read but not
  // honored. We surface this prominently so they're not surprised when
  // the generated schedule still has e.g. 1 senior on Take-Off instead
  // of the 2 they just typed.
  const isEveryoneOnce =
    (schedule?.fairnessMode || policy?.fairnessMode) === "everyone_once";

  const [overrides, setOverrides] = useState({});
  const [excluded, setExcluded] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeTeam, setActiveTeam] = useState(null);

  const teamsInDept = useMemo(() => {
    const counts = new Map();
    for (const e of employees || []) {
      if ((e?.employeeStatus || "").toLowerCase() !== "aktiv") continue;
      if (e?.employeeDepartmentId !== schedule?.departmentId) continue;
      const t = e?.employeeTeam || TEAM_NONE;
      counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [employees, schedule?.departmentId]);

  useEffect(() => {
    if (!open) return;
    setOverrides(schedule?.quotaOverrides || {});
    setExcluded(
      Array.isArray(schedule?.excludedEmployeeIdsOverride)
        ? schedule.excludedEmployeeIdsOverride
        : Array.isArray(policy?.excludedEmployeeIds)
          ? [...policy.excludedEmployeeIds]
          : []
    );
    setActiveTeam(teamsInDept[0]?.name || null);
  }, [open, schedule, policy, teamsInDept]);

  const rotateRules = useMemo(
    () => (policy?.roleRules || []).filter((r) => r.mode === "rotate"),
    [policy]
  );

  const exclusionEmployees = useMemo(
    () =>
      (employees || []).filter(
        (e) =>
          e?.employeeDepartmentId === schedule?.departmentId &&
          (e?.employeeStatus || "").toLowerCase() === "aktiv"
      ),
    [employees, schedule?.departmentId]
  );

  const excludedSet = useMemo(() => new Set(excluded || []), [excluded]);
  const policyExcludedSet = useMemo(
    () => new Set(policy?.excludedEmployeeIds || []),
    [policy]
  );

  // Count how many quota cells differ from the policy default.
  const modifiedCount = useMemo(() => {
    let n = 0;
    for (const [rid, teams] of Object.entries(overrides || {})) {
      const rule = rotateRules.find((r) => r.roleId === rid);
      if (!rule) continue;
      for (const [team, ovr] of Object.entries(teams || {})) {
        if (!ovr) continue;
        const base = resolveQuota(rule, team, null);
        if (
          Number(ovr.minSeniors || 0) !== base.minSeniors ||
          Number(ovr.minJuniors || 0) !== base.minJuniors
        ) {
          n += 1;
        }
      }
    }
    return n;
  }, [overrides, rotateRules]);

  // How many people are excluded this month who weren't excluded by
  // the master policy (and vice versa).
  const excludeDiff = useMemo(() => {
    const added = excluded.filter((id) => !policyExcludedSet.has(id));
    const removed = (policy?.excludedEmployeeIds || []).filter(
      (id) => !excludedSet.has(id)
    );
    return { added, removed };
  }, [excluded, excludedSet, policy, policyExcludedSet]);

  // Patches & helpers
  const patchQuota = (roleId, team, field, value) => {
    setOverrides((prev) => {
      const next = { ...prev };
      const roleMap = { ...(next[roleId] || {}) };
      const cell = { ...(roleMap[team] || {}) };
      cell[field] = Math.max(0, Number(value || 0));
      roleMap[team] = cell;
      next[roleId] = roleMap;
      return next;
    });
  };

  const resetTeam = () => {
    setOverrides((prev) => {
      const next = { ...prev };
      for (const roleId of Object.keys(next)) {
        if (next[roleId] && next[roleId][activeTeam]) {
          const { [activeTeam]: _drop, ...rest } = next[roleId];
          next[roleId] = rest;
          if (Object.keys(next[roleId]).length === 0) delete next[roleId];
        }
      }
      return next;
    });
    message.info(
      `Vlerat e ekipit ${activeTeam === TEAM_NONE ? "(pa ekip)" : activeTeam} u rivendosën.`
    );
  };

  const resetAll = () => {
    setOverrides({});
    setExcluded([...(policy?.excludedEmployeeIds || [])]);
    message.info("Të gjitha vlerat u rivendosën te politika bazë.");
  };

  const callSave = async ({ regenerate }) => {
    if (!schedule?.scheduleId) return;
    setSaving(true);
    try {
      // Strip empty role maps so we don't store noise.
      const cleanOverrides = {};
      for (const [rid, teams] of Object.entries(overrides)) {
        const t = {};
        for (const [team, q] of Object.entries(teams)) {
          if (q && (q.minSeniors > 0 || q.minJuniors > 0)) t[team] = q;
        }
        if (Object.keys(t).length) cleanOverrides[rid] = t;
      }

      const saveRes = await API.put(
        "shiftSchedules",
        `/shiftSchedules/${encodeURIComponent(schedule.scheduleId)}/quotas`,
        {
          body: {
            quotaOverrides: cleanOverrides,
            excludedEmployeeIdsOverride: excluded,
          },
        }
      );
      if (saveRes?.ok === false) {
        throw new Error(saveRes.error || "save_failed");
      }
      message.success("Parametrat e muajit u ruajtën.");
      onSaved?.(saveRes.schedule);

      if (regenerate) {
        const genRes = await API.post("shiftSchedules", "/shiftSchedules/generate", {
          body: {
            departmentId: schedule.departmentId,
            yearMonth: schedule.yearMonth,
          },
        });
        if (genRes?.ok === false) {
          throw new Error(genRes.error || "generate_failed");
        }
        message.success("Kalendari u rigjenerua me parametrat e rinj.");
        onSavedAndRegenerated?.(genRes.schedule);
      }
      onClose?.();
    } catch (e) {
      console.error(e);
      message.error(`Operacioni dështoi: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const totalActive = teamsInDept.reduce((s, t) => s + t.count, 0);

  return (
    <Drawer
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            Parametrat për {ymToLabel(schedule?.yearMonth || "")}
          </span>
          {modifiedCount > 0 || excludeDiff.added.length > 0 || excludeDiff.removed.length > 0 ? (
            <Tag color="gold">të pa-ruajtura</Tag>
          ) : (
            <Tag color="default">në sinkron me politikën</Tag>
          )}
        </div>
      }
      open={open}
      onClose={onClose}
      width={780}
      destroyOnClose
      className="month-params-drawer"
      rootClassName={isDarkMode ? "dark" : "light"}
      footer={
        <div className="month-params-drawer__footer">
          <div className="month-params-drawer__footer-summary">
            {modifiedCount > 0 || excludeDiff.added.length > 0 ? (
              <>
                <Tag color="gold">
                  {modifiedCount} ndryshim{modifiedCount === 1 ? "" : "e"} kuotash
                </Tag>
                {excludeDiff.added.length > 0 ? (
                  <Tag color="orange">+{excludeDiff.added.length} përjashtim</Tag>
                ) : null}
                {excludeDiff.removed.length > 0 ? (
                  <Tag color="cyan">-{excludeDiff.removed.length} përjashtim</Tag>
                ) : null}
              </>
            ) : (
              <Tag icon={<CheckCircleFilled />} color="success">
                Pa modifikime
              </Tag>
            )}
          </div>
          <div className="month-params-drawer__footer-actions">
            <MondayButton className="mondayButtonRed" onClick={onClose}>
              Anulo
            </MondayButton>
            <MondayButton
              className="mondayButtonBlue"
              spin={saving}
              onClick={() => callSave({ regenerate: false })}
            >
              Ruaj
            </MondayButton>
            <MondayButton
              className="mondayButtonGreen"
              spin={saving}
              onClick={() => callSave({ regenerate: true })}
            >
              Ruaj & Rigjenero
            </MondayButton>
          </div>
        </div>
      }
    >
      {/* Summary card */}
      <Card size="small" className="month-params-summary">
        <div className="month-params-summary__row">
          <div className="month-params-summary__stat">
            <div className="month-params-summary__num">{totalActive}</div>
            <div className="month-params-summary__lbl">punonjës aktivë</div>
          </div>
          <div className="month-params-summary__stat">
            <div className="month-params-summary__num">{teamsInDept.length}</div>
            <div className="month-params-summary__lbl">
              ekip{teamsInDept.length === 1 ? "" : "e"}
            </div>
          </div>
          <div className="month-params-summary__stat">
            <div className="month-params-summary__num">{rotateRules.length}</div>
            <div className="month-params-summary__lbl">role rotative</div>
          </div>
          <div className="month-params-summary__stat">
            <div className="month-params-summary__num">{excluded.length}</div>
            <div className="month-params-summary__lbl">të përjashtuar</div>
          </div>
          <div style={{ flex: 1 }} />
          <Tooltip title="Rivendos të gjitha kuotat dhe përjashtimet te vlerat e politikës bazë">
            <a onClick={resetAll} className="month-params-summary__reset">
              <ReloadOutlined /> Rivendos gjithçka
            </a>
          </Tooltip>
        </div>
        <Alert
          type="info"
          showIcon
          message={
            <>
              Këto parametra zbatohen <b>vetëm për {ymToLabel(schedule?.yearMonth || "")}</b>.
              Politika bazë mbetet e pandryshuar.
            </>
          }
          style={{ marginTop: 10 }}
        />
        {isEveryoneOnce ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 10 }}
            message={
              <b>
                Modaliteti aktual i drejtësisë është "Çdo punonjës nga 1 javë"
              </b>
            }
            description={
              <span>
                Në këtë modalitet, secili punonjës i kualifikuar merr{" "}
                <b>saktësisht 1 javë pasdite në muaj</b>. Kuotat poshtë
                (<b>min. seniorë / juniorë në javë</b>){" "}
                <b>NUK do të aplikohen</b> nga algoritmi —
                mbulimi i rolit për javë është pasojë e shpërndarjes.
                {onEditPolicy ? (
                  <>
                    {" "}
                    <a onClick={onEditPolicy} style={{ fontWeight: 600 }}>
                      Ndrysho modalitetin te politika bazë →
                    </a>
                  </>
                ) : null}
              </span>
            }
          />
        ) : null}
      </Card>

      {/* Section 1: Quotas */}
      <div
        className={`month-params-section ${
          isEveryoneOnce ? "month-params-section--inactive" : ""
        }`}
      >
        <div className="month-params-section__title">
          1 · Kuotat pasdite për çdo rol &amp; ekip
          {isEveryoneOnce ? (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              Nuk zbatohen në modalitetin aktual
            </Tag>
          ) : null}
        </div>

        {teamsInDept.length === 0 ? (
          <Empty description="Nuk u gjetën punonjës aktivë në këtë departament." />
        ) : rotateRules.length === 0 ? (
          <Empty description='Asnjë rol me mënyrën "Rotacion" — caktoni rolet te politika bazë së pari.' />
        ) : (
          <>
            {teamsInDept.length > 1 ? (
              <Tabs
                activeKey={activeTeam || teamsInDept[0]?.name}
                onChange={setActiveTeam}
                size="small"
                items={teamsInDept.map((t) => ({
                  key: t.name,
                  label: (
                    <span>
                      {t.name === TEAM_NONE ? "Pa ekip" : t.name}{" "}
                      <Tag style={{ marginLeft: 4 }}>{t.count}</Tag>
                    </span>
                  ),
                }))}
                tabBarExtraContent={
                  <a onClick={resetTeam} style={{ fontSize: 12 }}>
                    <ReloadOutlined /> Rivendos këtë ekip
                  </a>
                }
              />
            ) : null}

            <div className="role-cards">
              {rotateRules.map((rule) => (
                <RoleCard
                  key={rule.roleId}
                  rule={rule}
                  team={activeTeam || teamsInDept[0]?.name}
                  overrides={overrides}
                  employees={exclusionEmployees}
                  excludedSet={excludedSet}
                  onPatch={patchQuota}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Section 2: Exclusions */}
      <div className="month-params-section">
        <div className="month-params-section__title">
          2 · Përjashtime nga rotacioni
        </div>
        <p className="month-params-section__hint">
          Lista nis nga politika bazë; ndryshimet vlejnë vetëm për{" "}
          {ymToLabel(schedule?.yearMonth || "")}.
        </p>

        <Select
          mode="multiple"
          allowClear
          showSearch
          placeholder="Shto / hiq punonjës nga rotacioni i këtij muaji"
          value={excluded}
          onChange={setExcluded}
          style={{ width: "100%" }}
          optionFilterProp="label"
          classNames={{ popup: { root: isDarkMode ? "darkDropdown" : "" } }}
          options={exclusionEmployees.map((e) => ({
            value: e.employeeId,
            label: `${e.employeeFirstName} ${e.employeeLastName} · ${e.employeeRoleName || "?"} · ${e.employeeTeam || "—"}`,
          }))}
          tagRender={(props) => {
            const isInherited = policyExcludedSet.has(props.value);
            return (
              <Tag
                color={isInherited ? "default" : "orange"}
                closable={props.closable}
                onClose={props.onClose}
                style={{ marginRight: 4 }}
              >
                {isInherited ? null : <UserDeleteOutlined style={{ marginRight: 4 }} />}
                {props.label}
                {isInherited ? (
                  <span style={{ color: "#999", marginLeft: 6, fontSize: 11 }}>
                    politikë
                  </span>
                ) : null}
              </Tag>
            );
          }}
        />

        {excludeDiff.removed.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 8 }}
            message={`${excludeDiff.removed.length} punonjës që janë të përjashtuar nga politika do të rikthehen në rotacion vetëm këtë muaj.`}
          />
        ) : null}
      </div>
    </Drawer>
  );
}

// Per (role, team) card with live eligibility feedback.
function RoleCard({ rule, team, overrides, employees, excludedSet, onPatch }) {
  const effective = resolveQuota(rule, team, overrides);
  const base = resolveQuota(rule, team, null);
  const { seniors, juniors } = useMemo(
    () => splitEligible(employees, rule, team, excludedSet),
    [employees, rule, team, excludedSet]
  );

  const srShort = effective.minSeniors > seniors.length;
  const jrShort = effective.minJuniors > juniors.length;
  const isModified =
    effective.minSeniors !== base.minSeniors ||
    effective.minJuniors !== base.minJuniors;
  const willPickSomeone =
    effective.minSeniors > 0 || effective.minJuniors > 0;

  return (
    <Card
      size="small"
      className={`role-card ${isModified ? "role-card--modified" : ""}`}
      title={
        <div className="role-card__title">
          <span>{rule.roleName}</span>
          {isModified ? (
            <Tag color="gold" style={{ marginLeft: 8 }}>
              modifikuar
            </Tag>
          ) : null}
          {!willPickSomeone ? (
            <Tag style={{ marginLeft: 8 }}>asnjë pasdite</Tag>
          ) : null}
        </div>
      }
      extra={
        <Tooltip
          title={
            <>
              Eligible në {team === TEAM_NONE ? "(pa ekip)" : team}:
              <br />• {seniors.length} senior(ë) (≥ {rule.minYearsExperience || 0} vite)
              <br />• {juniors.length} junior(ë)
            </>
          }
        >
          <span style={{ color: "#888", fontSize: 12, cursor: "help" }}>
            {seniors.length} sr · {juniors.length} jr <InfoCircleOutlined />
          </span>
        </Tooltip>
      }
    >
      <div className="role-card__row">
        <label>
          <span className="role-card__lbl">Seniorë / javë</span>
          <Tooltip
            title={`Min. ${rule.minYearsExperience || 0} vite eksperiencë.`}
          >
            <InfoCircleOutlined style={{ color: "#bbb", marginLeft: 4 }} />
          </Tooltip>
        </label>
        <InputNumber
          min={0}
          max={20}
          value={effective.minSeniors}
          onChange={(v) => onPatch(rule.roleId, team, "minSeniors", v)}
          status={srShort ? "warning" : undefined}
          style={{ width: 84 }}
        />
        <span className="role-card__hint">
          {srShort ? (
            <span style={{ color: "#fa8c16" }}>
              <WarningOutlined /> kërkesa &gt; eligible ({seniors.length})
            </span>
          ) : (
            <span style={{ color: "#888" }}>nga {seniors.length} eligible</span>
          )}
        </span>
      </div>

      <div className="role-card__row">
        <label>
          <span className="role-card__lbl">Juniorë / javë</span>
        </label>
        <InputNumber
          min={0}
          max={20}
          value={effective.minJuniors}
          onChange={(v) => onPatch(rule.roleId, team, "minJuniors", v)}
          status={jrShort ? "warning" : undefined}
          style={{ width: 84 }}
        />
        <span className="role-card__hint">
          {jrShort ? (
            <span style={{ color: "#fa8c16" }}>
              <WarningOutlined /> kërkesa &gt; eligible ({juniors.length})
            </span>
          ) : (
            <span style={{ color: "#888" }}>nga {juniors.length} eligible</span>
          )}
        </span>
      </div>

      <div className="role-card__policy">
        Politika bazë: <b>{base.minSeniors} sr</b> + <b>{base.minJuniors} jr</b>
        {isModified ? (
          <a
            onClick={() => {
              onPatch(rule.roleId, team, "minSeniors", base.minSeniors);
              onPatch(rule.roleId, team, "minJuniors", base.minJuniors);
            }}
            style={{ marginLeft: 8 }}
          >
            <ReloadOutlined /> rikthe te politika
          </a>
        ) : null}
      </div>

      {willPickSomeone && (seniors.length > 0 || juniors.length > 0) ? (
        <div className="role-card__pool">
          <small style={{ color: "#888" }}>
            Eligible:{" "}
            {[...seniors, ...juniors]
              .map((e) => {
                const y = yearsOfService(e).toFixed(1);
                const tier = seniors.includes(e) ? "sr" : "jr";
                return `${e.employeeFirstName} ${e.employeeLastName} (${tier} · ${y}v)`;
              })
              .join(", ")}
          </small>
        </div>
      ) : null}
    </Card>
  );
}

export default MonthParamsDrawer;
